import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Phone } from "lucide-react";
import { saveReturnUrl, consumeReturnUrl, defaultDashboardFor } from "@/lib/postLoginRedirect";

export default function Register() {
  const { register, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "student" });

  // Mirror any return URL into sessionStorage on mount.
  React.useEffect(() => {
    const fromState = loc.state?.from?.pathname;
    if (fromState) saveReturnUrl(fromState);
  }, [loc.state]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name}!`);
      const dest = consumeReturnUrl() || loc.state?.from?.pathname || defaultDashboardFor(u.role);
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight">Join the batch.</h1>
        <p className="mt-3 text-[var(--text-dim)] max-w-md">Sign up free — explore courses, take demo classes & enroll into paid batches when ready.</p>
        <div className="mt-8 glass rounded-2xl p-5">
          <div className="text-sm font-medium">What you get</div>
          <ul className="mt-3 space-y-2 text-sm text-[var(--text-dim)] list-disc pl-4">
            <li>Free demo classes</li>
            <li>Live HD sessions + auto recordings</li>
            <li>iPad whiteboard teaching</li>
            <li>Doubt support & assignments</li>
          </ul>
        </div>
      </div>

      <form onSubmit={submit} className="glass-strong rounded-3xl p-6 sm:p-8" data-testid="register-form">
        <div className="font-display text-2xl font-semibold">Create account</div>
        <p className="text-sm text-[var(--text-mute)] mt-1">Takes under 30 seconds</p>
        <div className="mt-6 space-y-4">
          <Field icon={User} label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your name" required testId="reg-name" />
          <Field icon={Mail} type="email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" required testId="reg-email" />
          <Field icon={Lock} type="password" label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="At least 6 characters" required testId="reg-password" />
          <Field icon={Phone} label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+91…" testId="reg-phone" />

          <label className="block">
            <div className="text-xs text-[var(--text-dim)] mb-1.5">I am a</div>
            <div className="grid grid-cols-3 gap-2">
              {["student", "teacher"].map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => setForm({ ...form, role: r })}
                  className={`px-3 py-2 rounded-xl text-sm border transition ${
                    form.role === r ? "border-fuchsia-400/60 bg-fuchsia-400/10 text-fuchsia-200" : "border-white/10 hover:bg-white/5"
                  }`}
                  data-testid={`role-${r}`}
                >
                  {r[0].toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
          </label>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </button>
          <div className="text-center text-sm text-[var(--text-dim)]">
            Already have one? <Link to="/login" className="text-fuchsia-300 hover:underline">Log in</Link>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <label className="block">
      <div className="text-xs text-[var(--text-dim)] mb-1.5">{label}</div>
      <div className="relative">
        <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-mute)]" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-fuchsia-400/40"
        />
      </div>
    </label>
  );
}
